import { useState, useEffect, useRef } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { useLanguage } from "@/hooks/useLanguage";
const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNewsDropdownOpen, setIsNewsDropdownOpen] = useState(false);
  const [isCareersDropdownOpen, setIsCareersDropdownOpen] = useState(false);
  const { language, setLanguage, t } = useLanguage();
  const newsDropdownRef = useRef<HTMLDivElement>(null);
  const careersDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (newsDropdownRef.current && !newsDropdownRef.current.contains(event.target as Node)) {
        setIsNewsDropdownOpen(false);
      }
      if (careersDropdownRef.current && !careersDropdownRef.current.contains(event.target as Node)) {
        setIsCareersDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const navItems = [
    { name: t("header.about"), href: "/about" },
    { name: "Homes", href: "/portfolio/homes" },
    { name: "Hotels", href: "/portfolio/hotels" },
    { name: "Developments", href: "/portfolio/developments" },
    { name: "Technologies", href: "/portfolio/technologies" }
  ];

  const newsItems = [
    { name: "News", href: "/news-press?tab=news" },
    { name: "Press", href: "/news-press?tab=press" },
    { name: "Our Blog", href: "/news-press?tab=blog" }
  ];

  const careersItems = [
    { name: "Careers", href: "/careers" },
    { name: "Procurement", href: "/procurement" }
  ];


  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-gray-200 animate-fade-in-down">
      <div className="container mx-auto px-6 py-4">
        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center justify-between w-full">
          {/* Left Logo */}
          <div className="flex items-center animate-slide-in-left">
            <Link to="/" className="flex items-center">
              <img 
                src="/bhg-logo.png" 
                alt="BHG" 
                className="h-8 hover-scale" 
                width="156"
                height="32"
                loading="eager"
              />
            </Link>
          </div>

          {/* Right Navigation */}
          <div className="flex items-center space-x-8 animate-slide-in-right">
            {navItems.map((item, index) => (
              <Link
                key={item.href}
                to={item.href}
                className="text-black hover:text-charcoal transition-all duration-300 font-medium whitespace-nowrap relative group"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {item.name}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
              </Link>
            ))}
            
            {/* News Dropdown */}
            <div className="relative" ref={newsDropdownRef}>
              <button
                onClick={() => setIsNewsDropdownOpen(!isNewsDropdownOpen)}
                className="flex items-center gap-1 text-black hover:text-charcoal transition-all duration-300 font-medium whitespace-nowrap relative group"
              >
                News
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isNewsDropdownOpen ? 'rotate-180' : ''}`} />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
              </button>
              
              {/* Dropdown Menu */}
              {isNewsDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[60]">
                  <div className="py-2">
                    {newsItems.map((item, index) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-4 py-2 text-black hover:bg-gray-50 hover:text-charcoal transition-colors duration-200"
                        onClick={() => setIsNewsDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            {/* Careers Dropdown */}
            <div className="relative" ref={careersDropdownRef}>
              <button
                onClick={() => setIsCareersDropdownOpen(!isCareersDropdownOpen)}
                className="flex items-center gap-1 text-black hover:text-charcoal transition-all duration-300 font-medium whitespace-nowrap relative group"
              >
                Join Us
                <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isCareersDropdownOpen ? 'rotate-180' : ''}`} />
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
              </button>
              
              {/* Dropdown Menu */}
              {isCareersDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-[60]">
                  <div className="py-2">
                    {careersItems.map((item, index) => (
                      <Link
                        key={item.href}
                        to={item.href}
                        className="block px-4 py-2 text-black hover:bg-gray-50 hover:text-charcoal transition-colors duration-200"
                        onClick={() => setIsCareersDropdownOpen(false)}
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <Link
              to="/contact"
              className="text-black hover:text-charcoal transition-all duration-300 font-medium whitespace-nowrap relative group"
            >
              {t("header.contact")}
              <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-black transition-all duration-300 group-hover:w-full"></span>
            </Link>
            <div className="animate-scale-in" style={{ animationDelay: '0.6s' }}>
            </div>
          </div>
        </nav>

        {/* Mobile Navigation */}
        <div className="lg:hidden flex items-center justify-between">
          <Link to="/" className="animate-slide-in-left">
            <img 
              src="/bhg-logo.png" 
              alt="BHG" 
              className="h-8 w-auto cursor-pointer hover:opacity-80 transition-opacity hover-scale" 
              width="156"
              height="32"
              loading="eager"
            />
          </Link>
          
          <div className="flex items-center space-x-4 animate-slide-in-right">
            
            <button 
              className="text-black hover:text-charcoal transition-all duration-300 hover-scale p-2" 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu */}
        {isMenuOpen && (
          <div className="lg:hidden absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-xl border border-gray-200 mx-2 z-50 animate-fade-in-up max-h-[calc(100vh-100px)] overflow-y-auto">
            <div className="flex flex-col space-y-4 p-4">
              {navItems.map((item, index) => (
                <Link
                  key={item.href}
                  to={item.href}
                  className="text-black hover:text-charcoal transition-all duration-300 font-medium text-base py-3 px-4 hover:bg-gray-50 rounded-lg hover-lift"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
              
              {/* Mobile Careers Section */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-500 mb-2 px-4">Careers</p>
                {careersItems.map((item, index) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-black hover:text-charcoal transition-all duration-300 font-medium text-base py-3 px-4 hover:bg-gray-50 rounded-lg hover-lift block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              
              {/* Mobile News Section */}
              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-500 mb-2 px-4">News & Press</p>
                {newsItems.map((item, index) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    className="text-black hover:text-charcoal transition-all duration-300 font-medium text-base py-3 px-4 hover:bg-gray-50 rounded-lg hover-lift block"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
              
              <Link
                to="/contact"
                className="text-black hover:text-charcoal transition-all duration-300 font-medium text-base py-3 px-4 hover:bg-gray-50 rounded-lg hover-lift"
                onClick={() => setIsMenuOpen(false)}
              >
                {t("header.contact")}
              </Link>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};
export default Header;